import React, { useState, useRef, useEffect, useMemo } from "react";
import Input from "components/ui/Input";
import Button from "components/ui/Button";
import Icon from "components/AppIcon";
import { supabase } from "lib/supabase";

const NW_ENGLAND_SCHOOLS = [
  "Academy@Worden, Leyland",
  "Albany Academy, Chorley",
  "All Hallows Catholic High School, Penwortham",
  "Archbishop Temple School, Preston",
  "Ashton Community Science College, Preston",
  "Balshaw's Church of England High School, Leyland",
  "Bishop Rawstorne Church of England Academy, Croston",
  "Blackburn Central High School, Blackburn",
  "Broughton High School, Preston",
  "Brownedge St Mary's Catholic High School, Bamber Bridge",
  "Burscough Priory Academy, Burscough",
  "Canon Slade School, Bolton",
  "Christ the King Catholic High School, Preston",
  "Corpus Christi Catholic High School, Fulwood",
  "Deanery High School, Wigan",
  "Fulwood Academy, Preston",
  "Holy Cross Catholic High School, Chorley",
  "Hutton Grammar School, Hutton",
  "Lathom High School, Skelmersdale",
  "Lostock Hall Academy, Lostock Hall",
  "Moor Park High School, Preston",
  "Our Lady's Catholic High School, Fulwood",
  "Our Lady Queen of Peace Catholic Engineering College, Skelmersdale",
  "Parklands High School, Chorley",
  "Penwortham Girls' High School, Penwortham",
  "Penwortham Priory Academy, Penwortham",
  "Preston Muslim Girls High School, Preston",
  "Rivington and Blackrod High School, Horwich",
  "Southlands High School, Chorley",
  "St Bede's Catholic High School, Ormskirk",
  "St James's CE High School, Farnworth",
  "St Mary's Catholic High School, Leyland",
  "St Michael's CE High School, Chorley",
  "St Wilfrid's CE Academy, Blackburn",
  "Tarleton Academy, Tarleton",
  "Up Holland High School, Orrell",
  "Walton-le-Dale High School, Bamber Bridge",
  "Wellfield Academy, Leyland",
  "Westhoughton High School, Bolton",
  "Blackburn College, Blackburn",
  "Bolton College, Bolton",
  "Cardinal Newman College, Preston",
  "Holy Cross Sixth Form College, Bury",
  "Myerscough College, Bilsborrow",
  "Preston College, Preston",
  "Runshaw College, Leyland",
  "St John Rigby College, Wigan",
  "Tauheedul Islam Boys' High School Sixth Form, Blackburn",
  "West Lancashire College, Skelmersdale",
  "Wigan & Leigh College, Wigan",
  "Winstanley College, Wigan",
];

const COUNTRY_CODES = [
  { code: "+44", country: "GB", label: "🇬🇧 +44" },
  { code: "+1",  country: "US", label: "🇺🇸 +1" },
  { code: "+353", country: "IE", label: "🇮🇪 +353" },
  { code: "+33",  country: "FR", label: "🇫🇷 +33" },
  { code: "+49",  country: "DE", label: "🇩🇪 +49" },
  { code: "+34",  country: "ES", label: "🇪🇸 +34" },
  { code: "+39",  country: "IT", label: "🇮🇹 +39" },
  { code: "+31",  country: "NL", label: "🇳🇱 +31" },
  { code: "+32",  country: "BE", label: "🇧🇪 +32" },
  { code: "+46",  country: "SE", label: "🇸🇪 +46" },
  { code: "+47",  country: "NO", label: "🇳🇴 +47" },
  { code: "+45",  country: "DK", label: "🇩🇰 +45" },
  { code: "+358", country: "FI", label: "🇫🇮 +358" },
  { code: "+48",  country: "PL", label: "🇵🇱 +48" },
  { code: "+61",  country: "AU", label: "🇦🇺 +61" },
  { code: "+64",  country: "NZ", label: "🇳🇿 +64" },
  { code: "+27",  country: "ZA", label: "🇿🇦 +27" },
  { code: "+91",  country: "IN", label: "🇮🇳 +91" },
  { code: "+86",  country: "CN", label: "🇨🇳 +86" },
  { code: "+81",  country: "JP", label: "🇯🇵 +81" },
  { code: "+82",  country: "KR", label: "🇰🇷 +82" },
  { code: "+55",  country: "BR", label: "🇧🇷 +55" },
  { code: "+52",  country: "MX", label: "🇲🇽 +52" },
  { code: "+971", country: "AE", label: "🇦🇪 +971" },
  { code: "+966", country: "SA", label: "🇸🇦 +966" },
];

// OTP_COOLDOWN in seconds before user can resend
const OTP_COOLDOWN = 60;

function EmailInput({ value, onChange, error, onVerified, verifiedEmail }) {
  const [otpStep, setOtpStep] = useState("idle"); // idle | sending | sent | verifying | verified | error
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  const isVerified = verifiedEmail && verifiedEmail === value?.trim();

  useEffect(() => {
    return () => { if (cooldownRef?.current) clearInterval(cooldownRef?.current); };
  }, []);

  function startCooldown() {
    setCooldown(OTP_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef?.current); return 0; }
        return c - 1;
      });
    }, 1000);
  }

  function handleEmailChange(e) {
    onChange(e);
    if (otpStep === "verified" || otpStep === "sent") {
      setOtpStep("idle");
      setOtpCode("");
      setOtpError("");
      onVerified("");
    }
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email?.trim() || "");
  }

  async function handleSendOtp() {
    if (!value || !isValidEmail(value)) {
      setOtpError("Please enter a valid email address first.");
      return;
    }
    setOtpStep("sending");
    setOtpError("");
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke("send-email-otp", {
        body: { email: value?.trim(), action: "send" }
      });
      if (fnError || data?.error) {
        setOtpError(fnError?.message || data?.error || "Failed to send code. Please try again.");
        setOtpStep("error");
      } else {
        setOtpStep("sent");
        startCooldown();
      }
    } catch (err) {
      setOtpError("Network error. Please try again.");
      setOtpStep("error");
    }
  }

  async function handleVerifyOtp() {
    if (!otpCode || otpCode?.length !== 6) {
      setOtpError("Please enter the 6-digit code.");
      return;
    }
    setOtpStep("verifying");
    setOtpError("");
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke("send-email-otp", {
        body: { email: value?.trim(), action: "verify", otp: otpCode }
      });
      if (fnError || data?.error) {
        setOtpError(fnError?.message || data?.error || "Invalid code. Please try again.");
        setOtpStep("sent");
      } else if (data?.verified) {
        setOtpStep("verified");
        setOtpError("");
        onVerified(value?.trim());
      }
    } catch (err) {
      setOtpError("Network error. Please try again.");
      setOtpStep("sent");
    }
  }

  return (
    <div>
      <label htmlFor="email" className="block text-sm font-medium text-foreground mb-1">
        Email Address
        <span className="text-destructive ml-1">*</span>
        {isVerified && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs text-success font-normal">
            <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
            Verified
          </span>
        )}
      </label>
      <input
        id="email"
        name="email"
        type="email"
        placeholder="e.g. jane.doe@email.com"
        value={value || ""}
        onChange={handleEmailChange}
        disabled={isVerified}
        maxLength={150}
        className={`w-full px-3 py-2 rounded-md border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors disabled:opacity-60 ${
          error ? "border-destructive focus:ring-destructive" : isVerified ? "border-success" : "border-input"
        }`}
      />
      {!isVerified && (
        <p className="mt-1 text-xs text-muted-foreground">We'll send your confirmation here</p>
      )}
      {/* Send OTP button (idle / error state) */}
      {!isVerified && (otpStep === "idle" || otpStep === "error") && (
        <div className="mt-2">
          <button
            type="button"
            onClick={handleSendOtp}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-medium disabled:opacity-50"
          >
            <Icon name="Mail" size={14} />
            Send verification code
          </button>
          {otpError && (
            <p className="mt-1 text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {otpError}
            </p>
          )}
        </div>
      )}
      {/* Sending spinner */}
      {otpStep === "sending" && (
        <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
          <Icon name="Loader" size={12} />
          Sending code to {value?.trim()}…
        </p>
      )}
      {/* OTP input (sent / verifying state) */}
      {(otpStep === "sent" || otpStep === "verifying") && (
        <div className="mt-3 p-3 bg-muted/40 rounded-lg border border-border">
          <p className="text-xs text-muted-foreground mb-2">
            A 6-digit code was sent to <span className="font-medium text-foreground">{value?.trim()}</span>. Enter it below:
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={otpCode}
              onChange={(e) => {
                setOtpCode(e?.target?.value?.replace(/\D/g, "")?.slice(0, 6));
                setOtpError("");
              }}
              className="w-32 px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={otpStep === "verifying" || otpCode?.length !== 6}
              className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {otpStep === "verifying" ? "Verifying…" : "Verify"}
            </button>
          </div>
          {otpError && (
            <p className="mt-1.5 text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {otpError}
            </p>
          )}
          <div className="mt-2">
            {cooldown > 0 ? (
              <p className="text-xs text-muted-foreground">Resend in {cooldown}s</p>
            ) : (
              <button
                type="button"
                onClick={handleSendOtp}
                className="text-xs text-primary hover:underline"
              >
                Resend code
              </button>
            )}
          </div>
        </div>
      )}
      {/* Verified state */}
      {isVerified && (
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs text-success flex items-center gap-1">
            <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
            Email address verified successfully
          </p>
          <button
            type="button"
            onClick={() => {
              setOtpStep("idle");
              setOtpCode("");
              setOtpError("");
              onVerified("");
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Change email
          </button>
        </div>
      )}
      {/* Field-level error */}
      {error && (
        <p className="mt-1 text-xs text-destructive flex items-center gap-1">
          <Icon name="AlertCircle" size={12} />
          {error}
        </p>
      )}
    </div>
  );
}

function PhoneInput({ value, onChange, error, onVerified, verifiedPhone }) {
  function parseInitial(val) {
    if (!val) return { dialCode: "+44", local: "" };
    for (const c of COUNTRY_CODES) {
      if (val?.startsWith(c?.code)) {
        return { dialCode: c?.code, local: val?.slice(c?.code?.length)?.trim() };
      }
    }
    return { dialCode: "+44", local: val };
  }

  const parsed = parseInitial(value);
  const [dialCode, setDialCode] = useState(parsed?.dialCode);
  const [localNumber, setLocalNumber] = useState(parsed?.local);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const containerRef = useRef(null);

  // OTP state
  const [otpStep, setOtpStep] = useState("idle"); // idle | sending | sent | verifying | verified | error
  const [otpCode, setOtpCode] = useState("");
  const [otpError, setOtpError] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const cooldownRef = useRef(null);

  // Compute the current international phone number
  function getInternational(local, dial) {
    const stripped = local?.replace(/\s/g, "")?.replace(/^0+/, "");
    return stripped ? `${dial}${stripped}` : "";
  }

  const internationalPhone = getInternational(localNumber, dialCode);
  const isVerified = verifiedPhone && verifiedPhone === internationalPhone;

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    return () => { if (cooldownRef?.current) clearInterval(cooldownRef?.current); };
  }, []);

  function startCooldown() {
    setCooldown(OTP_COOLDOWN);
    cooldownRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef?.current); return 0; }
        return c - 1;
      });
    }, 1000);
  }

  function handleLocalChange(e) {
    const raw = e?.target?.value?.replace(/[^\d\s\-().+]/g, "");
    setLocalNumber(raw);
    const international = getInternational(raw, dialCode);
    onChange({ target: { name: "mobile", value: international } });
    // Reset verification if number changes
    if (otpStep === "verified" || otpStep === "sent") {
      setOtpStep("idle");
      setOtpCode("");
      setOtpError("");
      onVerified("");
    }
  }

  function handleDialCodeSelect(code) {
    setDialCode(code);
    setDropdownOpen(false);
    const international = getInternational(localNumber, code);
    onChange({ target: { name: "mobile", value: international } });
    // Reset verification if country changes
    if (otpStep === "verified" || otpStep === "sent") {
      setOtpStep("idle");
      setOtpCode("");
      setOtpError("");
      onVerified("");
    }
  }

  async function handleSendOtp() {
    if (!internationalPhone || internationalPhone?.replace(/\D/g, "")?.length < 7) {
      setOtpError("Please enter a valid phone number first.");
      return;
    }
    setOtpStep("sending");
    setOtpError("");
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke("send-sms-otp", {
        body: { phone: internationalPhone, action: "send" }
      });
      if (fnError || data?.error) {
        setOtpError(fnError?.message || data?.error || "Failed to send code. Please try again.");
        setOtpStep("error");
      } else {
        setOtpStep("sent");
        startCooldown();
      }
    } catch (err) {
      setOtpError("Network error. Please try again.");
      setOtpStep("error");
    }
  }

  async function handleVerifyOtp() {
    if (!otpCode || otpCode?.length !== 6) {
      setOtpError("Please enter the 6-digit code.");
      return;
    }
    setOtpStep("verifying");
    setOtpError("");
    try {
      const { data, error: fnError } = await supabase?.functions?.invoke("send-sms-otp", {
        body: { phone: internationalPhone, action: "verify", otp: otpCode }
      });
      if (fnError || data?.error) {
        setOtpError(fnError?.message || data?.error || "Invalid code. Please try again.");
        setOtpStep("sent");
      } else if (data?.verified) {
        setOtpStep("verified");
        setOtpError("");
        onVerified(internationalPhone);
      }
    } catch (err) {
      setOtpError("Network error. Please try again.");
      setOtpStep("sent");
    }
  }

  const selectedEntry = COUNTRY_CODES?.find((c) => c?.code === dialCode) || COUNTRY_CODES?.[0];

  return (
    <div>
      <label htmlFor="mobile" className="block text-sm font-medium text-foreground mb-1">
        Mobile Number
        <span className="text-destructive ml-1">*</span>
        {isVerified && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs text-success font-normal">
            <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
            Verified
          </span>
        )}
      </label>
      <div ref={containerRef} className="flex gap-0 relative">
        {/* Country code selector */}
        <button
          type="button"
          onClick={() => setDropdownOpen((o) => !o)}
          disabled={isVerified}
          className={`flex items-center gap-1 px-3 py-2 border rounded-l-md bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors whitespace-nowrap disabled:opacity-60 ${
            error ? "border-destructive" : isVerified ? "border-success" : "border-input"
          }`}
          aria-haspopup="listbox"
          aria-expanded={dropdownOpen}
        >
          <span>{selectedEntry?.label}</span>
          <Icon name="ChevronDown" size={14} />
        </button>
        {/* Local number input */}
        <input
          id="mobile"
          name="mobile"
          type="tel"
          placeholder="7911 123456"
          value={localNumber}
          onChange={handleLocalChange}
          disabled={isVerified}
          maxLength={15}
          className={`flex-1 px-3 py-2 border-t border-b border-r rounded-r-md bg-background text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors disabled:opacity-60 ${
            error ? "border-destructive focus:ring-destructive" : isVerified ? "border-success" : "border-input"
          }`}
        />
        {/* Dropdown */}
        {dropdownOpen && (
          <ul
            role="listbox"
            className="absolute z-50 top-full left-0 mt-1 w-48 bg-background border border-border rounded-md shadow-lg max-h-60 overflow-y-auto"
          >
            {COUNTRY_CODES?.map((c) => (
              <li
                key={c?.code}
                role="option"
                aria-selected={c?.code === dialCode}
                onClick={() => handleDialCodeSelect(c?.code)}
                className={`px-3 py-2 text-sm cursor-pointer ${
                  c?.code === dialCode
                    ? "bg-primary text-primary-foreground"
                    : "text-foreground hover:bg-muted"
                }`}
              >
                {c?.label} {c?.country}
              </li>
            ))}
          </ul>
        )}
      </div>
      {/* Hint text */}
      {!isVerified && (
        <p className="mt-1 text-xs text-muted-foreground">
          UK format: 07911 123456 — stored as +447911123456
        </p>
      )}
      {/* Send OTP button (idle / error state) */}
      {!isVerified && (otpStep === "idle" || otpStep === "error") && (
        <div className="mt-2">
          <button
            type="button"
            onClick={handleSendOtp}
            className="inline-flex items-center gap-1.5 text-sm text-primary hover:underline font-medium disabled:opacity-50"
          >
            <Icon name="MessageSquare" size={14} />
            Send verification code
          </button>
          {otpError && (
            <p className="mt-1 text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {otpError}
            </p>
          )}
        </div>
      )}
      {/* Sending spinner */}
      {otpStep === "sending" && (
        <p className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
          <Icon name="Loader" size={12} />
          Sending code to {internationalPhone}…
        </p>
      )}
      {/* OTP input (sent / verifying state) */}
      {(otpStep === "sent" || otpStep === "verifying") && (
        <div className="mt-3 p-3 bg-muted/40 rounded-lg border border-border">
          <p className="text-xs text-muted-foreground mb-2">
            A 6-digit code was sent to <span className="font-medium text-foreground">{internationalPhone}</span>. Enter it below:
          </p>
          <div className="flex gap-2">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder="000000"
              value={otpCode}
              onChange={(e) => {
                setOtpCode(e?.target?.value?.replace(/\D/g, "")?.slice(0, 6));
                setOtpError("");
              }}
              className="w-32 px-3 py-2 border border-input rounded-md text-sm bg-background text-foreground text-center tracking-widest font-mono focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
            <button
              type="button"
              onClick={handleVerifyOtp}
              disabled={otpStep === "verifying" || otpCode?.length !== 6}
              className="px-4 py-2 bg-primary text-primary-foreground text-sm font-medium rounded-md hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {otpStep === "verifying" ? "Verifying…" : "Verify"}
            </button>
          </div>
          {otpError && (
            <p className="mt-1.5 text-xs text-destructive flex items-center gap-1">
              <Icon name="AlertCircle" size={12} />
              {otpError}
            </p>
          )}
          <div className="mt-2">
            {cooldown > 0 ? (
              <p className="text-xs text-muted-foreground">Resend in {cooldown}s</p>
            ) : (
              <button
                type="button"
                onClick={handleSendOtp}
                className="text-xs text-primary hover:underline"
              >
                Resend code
              </button>
            )}
          </div>
        </div>
      )}
      {/* Verified state */}
      {isVerified && (
        <div className="mt-2 flex items-center justify-between">
          <p className="text-xs text-success flex items-center gap-1">
            <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
            Phone number verified successfully
          </p>
          <button
            type="button"
            onClick={() => {
              setOtpStep("idle");
              setOtpCode("");
              setOtpError("");
              onVerified("");
            }}
            className="text-xs text-muted-foreground hover:text-foreground underline"
          >
            Change number
          </button>
        </div>
      )}
      {/* Field-level error */}
      {error && (
        <p className="mt-1 text-xs text-destructive flex items-center gap-1">
          <Icon name="AlertCircle" size={12} />
          {error}
        </p>
      )}
    </div>
  );
}

function SchoolAutocomplete({ value, onChange, error }) {
  const [query, setQuery] = useState(value || "");
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    setQuery(value || "");
  }, [value]);

  useEffect(() => {
    function handleClickOutside(e) {
      if (containerRef?.current && !containerRef?.current?.contains(e?.target)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleInputChange(e) {
    const val = e?.target?.value;
    setQuery(val);
    onChange({ target: { name: "school", value: val } });
    if (val?.trim()?.length >= 2) {
      const filtered = NW_ENGLAND_SCHOOLS?.filter((s) =>
        s?.toLowerCase()?.includes(val?.toLowerCase())
      )?.slice(0, 8);
      setSuggestions(filtered);
      setIsOpen(filtered?.length > 0);
    } else {
      setSuggestions([]);
      setIsOpen(false);
    }
    setHighlightedIndex(-1);
  }

  function handleSelect(school) {
    setQuery(school);
    onChange({ target: { name: "school", value: school } });
    setSuggestions([]);
    setIsOpen(false);
    setHighlightedIndex(-1);
  }

  function handleKeyDown(e) {
    if (!isOpen) return;
    if (e?.key === "ArrowDown") {
      e?.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, suggestions?.length - 1));
    } else if (e?.key === "ArrowUp") {
      e?.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
    } else if (e?.key === "Enter" && highlightedIndex >= 0) {
      e?.preventDefault();
      handleSelect(suggestions?.[highlightedIndex]);
    } else if (e?.key === "Escape") {
      setIsOpen(false);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <label
        htmlFor="school"
        className="block text-sm font-medium text-foreground mb-1"
      >
        Current School / College
        <span className="text-destructive ml-1">*</span>
      </label>
      <div className="relative">
        <input
          ref={inputRef}
          id="school"
          name="school"
          type="text"
          autoComplete="off"
          placeholder="e.g. Greenfield Academy, Leyland"
          value={query}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onFocus={() => {
            if (suggestions?.length > 0) setIsOpen(true);
          }}
          maxLength={150}
          aria-autoComplete="list"
          aria-expanded={isOpen}
          aria-controls="school-suggestions"
          aria-activedescendant={
            highlightedIndex >= 0 ? `school-option-${highlightedIndex}` : undefined
          }
          className={`w-full px-3 py-2 pr-9 rounded-md border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
            error
              ? "border-destructive focus:ring-destructive" :"border-input"
          }`}
        />
        <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none text-muted-foreground">
          <Icon name="School" size={16} />
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        Enter your current educational institution in North West England
      </p>
      {error && (
        <p className="mt-1 text-xs text-destructive flex items-center gap-1">
          <Icon name="AlertCircle" size={12} />
          {error}
        </p>
      )}
      {isOpen && suggestions?.length > 0 && (
        <ul
          id="school-suggestions"
          role="listbox"
          className="absolute z-50 mt-1 w-full bg-background border border-border rounded-md shadow-lg max-h-56 overflow-y-auto"
        >
          {suggestions?.map((school, idx) => (
            <li
              key={school}
              id={`school-option-${idx}`}
              role="option"
              aria-selected={idx === highlightedIndex}
              onMouseDown={(e) => e?.preventDefault()}
              onClick={() => handleSelect(school)}
              onMouseEnter={() => setHighlightedIndex(idx)}
              className={`px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${
                idx === highlightedIndex
                  ? "bg-primary text-primary-foreground"
                  : "text-foreground hover:bg-muted"
              }`}
            >
              <Icon
                name="MapPin"
                size={13}
                color={idx === highlightedIndex ? "currentColor" : "var(--color-muted-foreground)"}
              />
              {school}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function RegistrationForm({ formData, errors, progress, isSubmitting, onChange, onSubmit, verifiedPhone, onVerified, verifiedEmail, onEmailVerified, submitError }) {
  // Calculate age from date of birth
  const calculatedAge = React.useMemo(() => {
    if (!formData?.dob) return null;
    const today = new Date();
    const birth = new Date(formData.dob);
    if (isNaN(birth?.getTime())) return null;
    let age = today?.getFullYear() - birth?.getFullYear();
    const monthDiff = today?.getMonth() - birth?.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birth?.getDate())) {
      age -= 1;
    }
    return age;
  }, [formData?.dob]);

  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);

  const showSchoolField = calculatedAge === null || calculatedAge <= 24;

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5">
      {/* Progress Bar */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="font-caption text-sm text-muted-foreground">Profile Completion</span>
          <span className="font-data text-sm font-semibold text-primary">{progress}%</span>
        </div>
        <div className="progress-bar-track">
          <div
            className="progress-bar-fill transition-all duration-500"
            style={{ width: `${progress}%` }}
            role="progressbar"
            aria-valuenow={progress}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`Form completion: ${progress}%`}
          />
        </div>
        {progress === 100 && (
          <p className="font-caption text-xs text-success mt-1 flex items-center gap-1">
            <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
            All fields complete — ready to register!
          </p>
        )}
      </div>
      {/* Name */}
      <Input
        label="Full Name"
        type="text"
        id="name"
        name="name"
        placeholder="e.g. Jane Doe"
        value={formData?.name}
        onChange={onChange}
        error={errors?.name}
        required
        maxLength={100}
      />
      {/* Date of Birth */}
      <Input
        label="Date of Birth"
        type="date"
        id="dob"
        name="dob"
        value={formData?.dob}
        onChange={onChange}
        error={errors?.dob}
        description="MM/DD/YYYY — You must be at least 16 years old"
        required
      />
      {/* Email with verification */}
      <EmailInput
        value={formData?.email}
        onChange={onChange}
        error={errors?.email}
        onVerified={onEmailVerified}
        verifiedEmail={verifiedEmail}
      />
      {/* Mobile with SMS verification */}
      <PhoneInput
        value={formData?.mobile}
        onChange={onChange}
        error={errors?.mobile}
        onVerified={onVerified}
        verifiedPhone={verifiedPhone}
      />
      {/* School / College — Autocomplete */}
      {showSchoolField && (
        <SchoolAutocomplete
          value={formData?.school}
          onChange={onChange}
          error={errors?.school}
        />
      )}
      {/* Password */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-foreground mb-1">
          Password
          <span className="text-destructive ml-1">*</span>
        </label>
        <div className="relative">
          <input
            id="password"
            name="password"
            type={showPassword ? "text" : "password"}
            placeholder="Minimum 8 characters"
            value={formData?.password || ""}
            onChange={onChange}
            maxLength={128}
            className={`w-full px-3 py-2 pr-10 rounded-md border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
              errors?.password ? "border-destructive focus:ring-destructive" : "border-input"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={showPassword ? "Hide password" : "Show password"}
          >
            <Icon name={showPassword ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>
        <p className="mt-1 text-xs text-muted-foreground">Must be at least 8 characters</p>
        {errors?.password && (
          <p className="mt-1 text-xs text-destructive flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {errors?.password}
          </p>
        )}
      </div>
      {/* Confirm Password */}
      <div>
        <label htmlFor="confirmPassword" className="block text-sm font-medium text-foreground mb-1">
          Confirm Password
          <span className="text-destructive ml-1">*</span>
        </label>
        <div className="relative">
          <input
            id="confirmPassword"
            name="confirmPassword"
            type={showConfirmPassword ? "text" : "password"}
            placeholder="Re-enter your password"
            value={formData?.confirmPassword || ""}
            onChange={onChange}
            maxLength={128}
            className={`w-full px-3 py-2 pr-10 rounded-md border text-sm bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary transition-colors ${
              errors?.confirmPassword ? "border-destructive focus:ring-destructive" : "border-input"
            }`}
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label={showConfirmPassword ? "Hide password" : "Show password"}
          >
            <Icon name={showConfirmPassword ? "EyeOff" : "Eye"} size={16} />
          </button>
        </div>
        {errors?.confirmPassword && (
          <p className="mt-1 text-xs text-destructive flex items-center gap-1">
            <Icon name="AlertCircle" size={12} />
            {errors?.confirmPassword}
          </p>
        )}
        {!errors?.confirmPassword && formData?.confirmPassword && formData?.password === formData?.confirmPassword && (
          <p className="mt-1 text-xs text-success flex items-center gap-1">
            <Icon name="CheckCircle2" size={12} color="var(--color-success)" />
            Passwords match
          </p>
        )}
      </div>
      {/* Submit error */}
      {submitError && (
        <div className="p-3 bg-destructive/10 border border-destructive/30 rounded-md">
          <p className="text-sm text-destructive flex items-center gap-2">
            <Icon name="AlertCircle" size={14} />
            {submitError}
          </p>
        </div>
      )}
      {/* Submit */}
      <div className="pt-2">
        <Button
          variant="default"
          size="lg"
          fullWidth
          type="submit"
          loading={isSubmitting}
          disabled={isSubmitting || !verifiedEmail || !verifiedPhone}
          iconName="UserPlus"
          iconPosition="left"
        >
          {isSubmitting ? "Registering…" : "Register as Volunteer"}
        </Button>
        {(!verifiedEmail || !verifiedPhone) && (
          <p className="mt-2 text-xs text-amber-600 flex items-center gap-1 justify-center">
            <Icon name="AlertTriangle" size={12} />
            {!verifiedEmail && !verifiedPhone
              ? "Please verify your email and mobile number before registering"
              : !verifiedEmail
              ? "Please verify your email address before registering"
              : "Please verify your mobile number before registering"}
          </p>
        )}
      </div>
    </form>
  );
}